import { VerificationCodeForm } from "./VerificationCodeForm";
import { TenantContactDetails } from "../tenant/TenantContactDetails";
import { useSiteConfig } from "../tenant/SiteConfigContext";
import { useEffect, useRef, useState } from "preact/hooks";
import { ErrorPanel, LoadingPanel } from "../../components/StatePanels";
import { maskEmail } from "../../lib/format";
import {
  useOnboardingFlow,
  type EmailEntryFlow,
  type EmailSelectionFlow,
  type CodeVerificationFlow,
  type InvoiceRecoveryFlow,
} from "./useOnboardingFlow";
import "./onboarding.css";

type Props = {
  providerName: string;
  onComplete: () => void;
  accessToken?: string;
  onAccessTokenRejected?: () => void;
};

export function OnboardingFlow({
  providerName,
  onComplete,
  accessToken,
  onAccessTokenRejected,
}: Props) {
  const flow = useOnboardingFlow({ onComplete, accessToken, onAccessTokenRejected });
  const [helpScreen, setHelpScreen] = useState<"office" | "qr" | null>(null);

  if (flow.step === "initializing")
    return flow.error ? (
      <ErrorPanel title="Unable to sign in" message={flow.error} />
    ) : (
      <LoadingPanel title="Checking your sign-in" />
    );

  return (
    <div class="onboarding-flow" aria-busy={flow.pending}>
      {helpScreen === "qr" ? (
        <QrSignInHelp
          onBack={() => setHelpScreen(null)}
          onInvoice={() => {
            setHelpScreen(null);
            flow.showInvoiceRecovery();
          }}
        />
      ) : helpScreen === "office" ? (
        <OfficeSignInHelp onBack={() => setHelpScreen(null)} />
      ) : (
        <>
          {flow.error && (
            <p class="payment-error" role="alert">
              {flow.error}
            </p>
          )}
          {flow.step === "invoiceRecovery" && <InvoiceRecovery flow={flow} />}
          {flow.step === "enterEmail" && <EmailEntry flow={flow} providerName={providerName} />}
          {flow.step === "selectEmail" && (
            <EmailSelection flow={flow} onOfficeHelp={() => setHelpScreen("office")} />
          )}
          {flow.step === "verify" && (
            <CodeVerification flow={flow} onOfficeHelp={() => setHelpScreen("office")} />
          )}
          {!flow.context && flow.step === "enterEmail" && (
            <button
              class="link-action"
              type="button"
              disabled={flow.pending}
              onClick={() => setHelpScreen("qr")}
            >
              Other sign-in options
            </button>
          )}
        </>
      )}
    </div>
  );
}

function ScreenHeading({ children }: { children: string }) {
  const heading = useRef<HTMLHeadingElement>(null);

  useEffect(() => {
    heading.current?.focus();
  }, []);

  return (
    <h2 ref={heading} tabIndex={-1}>
      {children}
    </h2>
  );
}

function OfficeSignInHelp({ onBack }: { onBack: () => void }) {
  const { displayName } = useSiteConfig();

  return (
    <>
      <button class="link-action" type="button" onClick={onBack}>
        <span aria-hidden="true">← </span>Back to sign in
      </button>
      <header class="onboarding-flow__heading">
        <ScreenHeading>Need help signing in?</ScreenHeading>
        <p class="muted">Contact {displayName} to update the email address on your account.</p>
      </header>
      <TenantContactDetails className="onboarding-contact" />
    </>
  );
}

function QrSignInHelp({ onBack, onInvoice }: { onBack: () => void; onInvoice: () => void }) {
  return (
    <>
      <button class="link-action" type="button" onClick={onBack}>
        <span aria-hidden="true">← </span>Back to email sign-in
      </button>
      <header class="onboarding-flow__heading">
        <ScreenHeading>Scan a QR code</ScreenHeading>
        <p class="muted">
          Use your phone to scan the QR code at the bottom of your invoice or statement.
        </p>
      </header>
      <div>
        <p class="muted">Look for a section like this:</p>
        <InvoicePaymentIllustration />
      </div>
      <button class="link-action" type="button" onClick={onInvoice}>
        I don't have a QR code
      </button>
    </>
  );
}

function InvoicePaymentIllustration() {
  // Decorative pattern only; it contains no encoded QR payload.
  const pattern = [
    "111110001011111",
    "100010100010001",
    "101010001010101",
    "100010100010001",
    "111110001011111",
    "000000000000000",
    "101000000001010",
    "010000000000101",
    "101000000001010",
    "000000000000000",
    "111110001010100",
    "100010100001011",
    "101010001010100",
    "100010100001011",
    "111110001010100",
  ];
  return (
    <div
      class="onboarding-invoice-preview"
      role="img"
      aria-label="Illustration of the Pay Online section, with a QR code on the left"
    >
      <div class="onboarding-invoice-preview__rule" />
      <div class="onboarding-invoice-preview__strip">
        <div class="onboarding-invoice-preview__code" aria-hidden="true">
          {pattern
            .join("")
            .split("")
            .map((cell, index) => (
              <span key={index} class={cell === "1" ? "is-filled" : undefined} />
            ))}
        </div>
        <div class="onboarding-invoice-preview__copy" aria-hidden="true">
          <strong>Pay Online</strong>
          <span>Scan QR code to view and pay your invoice online!</span>
        </div>
      </div>
      <div class="onboarding-invoice-preview__rule" />
    </div>
  );
}

function InvoiceRecovery({ flow }: { flow: InvoiceRecoveryFlow }) {
  const [invoiceCode, setInvoiceCode] = useState("");
  const [billingZip, setBillingZip] = useState("");
  const [amount, setAmount] = useState("");

  function submit(event: Event) {
    event.preventDefault();
    void flow.submitInvoiceProof({
      kind: "invoiceProof",
      invoiceCode: invoiceCode.trim(),
      billingZip: billingZip.trim(),
      invoiceTotal: Number(amount),
    });
  }

  return (
    <form class="onboarding-flow__form" onSubmit={submit}>
      <button class="link-action" type="button" disabled={flow.pending} onClick={flow.changeEmail}>
        <span aria-hidden="true">← </span>Back to email sign-in
      </button>
      <header class="onboarding-flow__heading">
        <ScreenHeading>Find your account</ScreenHeading>
        <p class="muted">Enter the following details from your invoice.</p>
      </header>
      <label class="form-control">
        <span>Invoice number</span>
        <input
          aria-label="Invoice number"
          required
          autoComplete="off"
          value={invoiceCode}
          disabled={flow.pending}
          onInput={(event) => setInvoiceCode(event.currentTarget.value)}
        />
      </label>
      <label class="form-control">
        <span>Billing ZIP code</span>
        <input
          aria-label="Billing ZIP code"
          required
          autoComplete="postal-code"
          value={billingZip}
          disabled={flow.pending}
          onInput={(event) => setBillingZip(event.currentTarget.value)}
        />
      </label>
      <label class="form-control">
        <span>Invoice amount ($)</span>
        <input
          aria-label="Invoice amount ($)"
          required
          type="number"
          min="0"
          step="0.01"
          inputMode="decimal"
          value={amount}
          disabled={flow.pending}
          onInput={(event) => setAmount(event.currentTarget.value)}
        />
      </label>
      <button class="primary-action" type="submit" disabled={flow.pending}>
        {flow.pending ? "Finding account…" : "Find account"}
      </button>
    </form>
  );
}

function EmailSelection({
  flow,
  onOfficeHelp,
}: {
  flow: EmailSelectionFlow;
  onOfficeHelp: () => void;
}) {
  return (
    <div class="onboarding-flow__form">
      <header class="onboarding-flow__heading">
        <ScreenHeading>Verify your email</ScreenHeading>
        <p class="muted">
          Choose an email address on your account. We'll send a code to confirm it's you.
        </p>
      </header>
      <div class="onboarding-flow__emails" aria-label="On-file email choices">
        {flow.emails.map((email) => (
          <button
            key={email}
            class="secondary-action"
            type="button"
            disabled={flow.pending}
            onClick={() => void flow.start(email)}
          >
            {maskEmail(email)}
          </button>
        ))}
      </div>
      <button class="link-action" type="button" disabled={flow.pending} onClick={onOfficeHelp}>
        {flow.emails.length === 1
          ? "I can't access this email address"
          : "I can't access these email addresses"}
      </button>
    </div>
  );
}

function EmailEntry({ flow, providerName }: { flow: EmailEntryFlow; providerName: string }) {
  const [email, setEmail] = useState(flow.email);
  function submit(event: Event) {
    event.preventDefault();
    void flow.start(email);
  }
  return (
    <form class="onboarding-flow__form" onSubmit={submit}>
      <header class="onboarding-flow__heading">
        <ScreenHeading>
          {flow.isEnrollingEmail ? "Add your email" : "Sign in to your account"}
        </ScreenHeading>
        <p class="muted">
          {flow.isEnrollingEmail
            ? "Your account doesn't have an email address. Add one to sign in online."
            : `Enter the email address you use with ${providerName}. We'll send you a sign-in code.`}
        </p>
      </header>
      <label class="form-control">
        <span>Email address</span>
        <input
          type="email"
          autoComplete="email"
          required
          value={email}
          disabled={flow.pending}
          onInput={(event) => setEmail(event.currentTarget.value)}
        />
      </label>
      <button class="primary-action" type="submit" disabled={flow.pending}>
        {flow.pending ? "Sending…" : "Send code"}
      </button>
    </form>
  );
}

function CodeVerification({
  flow,
  onOfficeHelp,
}: {
  flow: CodeVerificationFlow;
  onOfficeHelp: () => void;
}) {
  const email = flow.maskVerificationEmail ? maskEmail(flow.email) : flow.email;
  return (
    <>
      <VerificationCodeForm
        challenge={flow.challenge}
        pending={flow.pending}
        onSubmit={flow.signInWithCode}
        onResend={flow.resend}
        onChangeEmail={flow.changeEmail}
        submitLabel="Verify and continue"
      >
        <header class="onboarding-flow__heading">
          <ScreenHeading>Check your email</ScreenHeading>
          <p class="muted">
            {flow.context ? (
              <>
                We've sent a verification code to <strong>{email}</strong>.
              </>
            ) : (
              <>If {email} matches an account, we'll send a verification code.</>
            )}
          </p>
        </header>
        {flow.isEnrollingEmail && (
          <p class="muted">
            Verifying will save this as your account's primary email address and enable billing
            emails.
          </p>
        )}
      </VerificationCodeForm>
      <details class="onboarding-flow__help">
        <summary>Didn't receive a code?</summary>
        <p class="muted">
          Check your spam or junk folder and make sure the email address is correct. You can resend
          the code when the timer ends.
        </p>
        {flow.maskVerificationEmail ? (
          <button class="link-action" type="button" disabled={flow.pending} onClick={onOfficeHelp}>
            I can't access this email
          </button>
        ) : (
          !flow.context && (
            <button
              class="link-action"
              type="button"
              disabled={flow.pending}
              onClick={flow.showInvoiceRecovery}
            >
              Sign in with an invoice
            </button>
          )
        )}
      </details>
    </>
  );
}
